import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Compass,
  Inbox,
  Layers,
  UserRound,
  Building2,
  Hammer,
  Settings as SettingsIcon,
} from 'lucide-react';
import { AppProvider, useApp, type Destination } from '../../../packages/ui/src/provider';
import { Connections } from '../../../packages/ui/src/chat-list';
import { Ventures, VentureScreen } from '../../../packages/ui/src/ventures';
import { Settings } from '../../../packages/ui/src/settings';
import { ProfileScreen, Welcome } from '../../../packages/ui/src/founders';
import { DashboardScreen } from '../../../packages/ui/src/dashboard';
import { BoardroomScreen, PlanToolsScreen } from '../../../packages/ui/src/plan-tools';
import { ChatScreen, MeetingRoomScreen } from '../../../packages/ui/src/connections';
import { Loading, Page, Empty, Button } from '../../../packages/ui/src/components';
import { themes, CURRENT_USER, setCurrentUser } from '@ventureroom/core';
import { storage, preferences, storageForUser } from './storage';
import './style.css';
import { AccountScreen } from '../../../packages/ui/src/account';

// Allow ?user=<id> to switch the active demo user for this tab
const urlUser = import.meta.env.DEV
  ? new URLSearchParams(window.location.search).get('user')
  : null;
if (urlUser) setCurrentUser(urlUser);
const activeStorage = urlUser ? storageForUser(urlUser) : storage;
const activePrefs = urlUser
  ? {
      get: (k: string) => preferences.get(`${urlUser}:${k}`),
      set: (k: string, v: string) => preferences.set(`${urlUser}:${k}`, v),
    }
  : preferences;
const Discover = lazy(() =>
  import('../../../packages/ui/src/discovery').then((module) => ({ default: module.Discover })),
);
const links = [
  ['discover', 'Discover', Compass],
  ['connections', 'Inbox', Inbox],
  ['ventures', 'Ventures', Layers],
  ['profile', 'Profile', UserRound],
] as const;
function Screen({ route }: { route: string }) {
  const [name, id] = route.split('/');
  switch (name) {
    case 'discover':
      return <Discover />;
    case 'connections':
      return <Connections />;
    case 'ventures':
      return <Ventures />;
    case 'profile':
      return <ProfileScreen />;
    case 'dashboard':
      return <DashboardScreen />;
    case 'tools':
      return <PlanToolsScreen />;
    case 'boardroom':
      return <BoardroomScreen ventureId={id ?? ''} />;
    case 'settings':
      return <Settings />;
    case 'person':
      return <ProfileScreen personId={id} />;
    case 'venture':
      return <VentureScreen ventureId={id ?? ''} />;
    case 'chat':
      return <ChatScreen personId={id ?? ''} />;
    case 'meeting':
      return <MeetingRoomScreen ownerId={id ?? ''} />;
    default:
      return <Discover />;
  }
}
function Shell({ route }: { route: string }) {
  const app = useApp();
  const t = themes[app.theme];
  useEffect(() => {
    document.documentElement.dataset.theme = app.theme;
    document.documentElement.style.colorScheme = app.theme === 'dark' ? 'dark' : 'light';
    for (const [key, value] of Object.entries(t))
      if (typeof value === 'string') document.documentElement.style.setProperty(`--${key}`, value);
  }, [app.theme, t]);
  if (!app.ready) return <Loading />;
  if (!app.authenticated && !app.allowDemo) return <AccountScreen />;
  if (!app.data)
    return (
      <Page title="Reconnect">
        <Empty
          title="Workspace unavailable"
          detail={app.error}
          action={
            <>
              <Button onPress={() => void app.refresh()}>Try again</Button>
              <Button kind="quiet" onPress={() => void app.disconnect()}>
                Use offline workspace
              </Button>
            </>
          }
        />
      </Page>
    );
  if (!app.entered)
    return (
      <main className="welcome">
        <Welcome />
      </main>
    );
  return (
    <div
      className="app-shell"
      onClick={(event) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;
        const anchor = (event.target as Element).closest<HTMLAnchorElement>('a[href^="#/"]');
        if (anchor) {
          event.preventDefault();
          app.navigate(anchor.hash.slice(2) as Destination);
        }
      }}
    >
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <aside className="sidebar">
        <a href="#/discover" className="brand">
          <span>v.</span> VentureRoom
        </a>
        {urlUser && (
          <div
            style={{
              padding: '6px 8px',
              marginBottom: 4,
              borderRadius: 6,
              backgroundColor: t.accent,
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            Viewing as: {app.data?.profiles.find((p) => p.id === CURRENT_USER)?.name ?? urlUser}
          </div>
        )}
        <nav aria-label="Main navigation">
          {links.map(([id, label, Icon]) => (
            <a
              key={id}
              href={`#/${id}`}
              aria-current={route === id ? 'page' : undefined}
              className={route === id ? 'nav-link active' : 'nav-link'}
            >
              <Icon size={22} strokeWidth={1.6} />
              <span>{label}</span>
            </a>
          ))}
          {(() => {
            const me = app.data?.profiles.find((p) => p.id === CURRENT_USER);
            if (!me) return null;
            const isSkill = me.role === 'Skill Provider';
            if (isSkill) {
              return (
                <a
                  href="#/dashboard"
                  aria-current={route === 'dashboard' ? 'page' : undefined}
                  className={route === 'dashboard' ? 'nav-link active' : 'nav-link'}
                >
                  <Hammer size={22} strokeWidth={1.6} />
                  <span>Progression</span>
                </a>
              );
            }
            return (
              <a
                href="#/dashboard"
                aria-current={route === 'dashboard' ? 'page' : undefined}
                className={route === 'dashboard' ? 'nav-link active' : 'nav-link'}
              >
                <Building2 size={22} strokeWidth={1.6} />
                <span>Track Record</span>
              </a>
            );
          })()}
        </nav>
        <a className="settings-link" href="#/settings">
          <SettingsIcon size={18} />
          Settings & preferences
        </a>
      </aside>
      <div className="main-wrap">
        <main id="main" tabIndex={-1}>
          <Suspense fallback={<Loading />}>
            <Screen route={route} />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
function App() {
  const [route, setRoute] = useState(() => location.hash.slice(2) || 'discover');
  useEffect(() => {
    if (!Number.isInteger(history.state?.ventureRoomDepth))
      history.replaceState({ ventureRoomDepth: 0 }, '');
    const change = () => setRoute(location.hash.slice(2) || 'discover');
    window.addEventListener('hashchange', change);
    window.addEventListener('popstate', change);
    return () => {
      window.removeEventListener('hashchange', change);
      window.removeEventListener('popstate', change);
    };
  }, []);
  const navigate = useCallback((to: Destination) => {
    if (location.hash === `#/${to}`) return;
    history.pushState(
      { ventureRoomDepth: (history.state?.ventureRoomDepth ?? 0) + 1 },
      '',
      `#/${to}`,
    );
    setRoute(to);
  }, []);
  const goBack = useCallback((fallback: Destination) => {
    if ((history.state?.ventureRoomDepth ?? 0) > 0) history.back();
    else {
      history.replaceState({ ventureRoomDepth: 0 }, '', `#/${fallback}`);
      setRoute(fallback);
    }
  }, []);
  return (
    <AppProvider
      storage={activeStorage}
      preferences={activePrefs}
      navigate={navigate}
      goBack={goBack}
      allowDemo={import.meta.env.DEV && import.meta.env.VITE_ALLOW_DEMO === 'true'}
    >
      <Shell route={route} />
    </AppProvider>
  );
}
createRoot(document.getElementById('root')!).render(<App />);
